/**
 * FieldView (13-25): the record's columns, read only, in FIELDSET §3's stage order (`lib/recordView`). A blank reads
 * "Not recorded", never "No"; days read `D MMM YYYY` and instants add the Melbourne time; text areas keep their line
 * breaks (D-13-L-18); contact details sit only in their own section (§1.6); a frozen column shows only when it holds
 * a Centro value, and says so.
 *
 * 13.2-11 (D-13.2-A-5): each section collapses (`Collapsible`). The record page owns which are open (`open`,
 * `onToggle`, from `openByDefault`); without them every section starts open and toggles on its own. A closed
 * section says how many fields it holds, and how many are not recorded.
 */

import { useState } from 'react';
import { fieldSections } from '../../lib/recordView';
import type { FieldSection, SectionId } from '../../lib/recordView';
import { sectionHint } from '../../lib/recordSections';
import { NOT_RECORDED } from '../../lib/verbForm';
import type { RecordRow } from '../../types/records';
import Collapsible from './Collapsible';
import Lines from './Lines';
import styles from './record.module.css';

interface FieldViewProps {
  row: RecordRow;
  /** The sections to show (default `fieldSections(row)`), when the caller has already computed them. */
  sections?: readonly FieldSection[];
  /** The open sections; with `onToggle`, the caller owns the state. */
  open?: ReadonlySet<SectionId>;
  onToggle?: (id: SectionId) => void;
}

export default function FieldView({ row, sections: given, open, onToggle }: FieldViewProps) {
  const sections = given ?? fieldSections(row);
  const [own, setOwn] = useState<ReadonlySet<SectionId>>(() => new Set(sections.map((s) => s.id)));
  const isOpen = (id: SectionId) => (open ?? own).has(id);
  const toggle = (id: SectionId) => {
    if (onToggle) return onToggle(id);
    setOwn((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  return (
    <div className={styles.sections}>
      {sections.map((s) => (
        <Collapsible
          key={s.id}
          id={`fv-${s.id}`}
          className={styles.section}
          titleClassName={styles.sectionTitle}
          title={s.title}
          open={isOpen(s.id)}
          onToggle={() => toggle(s.id)}
          hint={sectionHint(s)}
        >
          <dl className={styles.facts}>
            {s.items.map((item) => (
              <div key={item.name} style={{ display: 'contents' }}>
                <dt>{item.label}</dt>
                <dd className={item.multiline ? styles.multiline : undefined}>
                  {item.value === null ? <span className={styles.blank}>{NOT_RECORDED}</span> : item.multiline ? <Lines text={item.value} /> : item.value}
                  {item.fromCentro && <span className={styles.centro}>From Centro</span>}
                </dd>
              </div>
            ))}
          </dl>
        </Collapsible>
      ))}
    </div>
  );
}
